import { useMemo } from "react";
import { BrainCircuit, Info } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { ScoreRing, DimensionBar } from "@/components/ScoreRing";
import { PageHeader, EmptyState } from "@/components/PageState";
import { SourceBadge } from "@/components/SourceBadge";
import { InsightPanel } from "@/components/InsightPanel";
import { Skeleton } from "@/components/ui/skeleton";

export default function AIReadiness() {
  const { data, loading } = useDashboard();
  const weakest = useMemo(
    () => (data?.ai_dimensions ? [...data.ai_dimensions].sort((a, b) => a.score - b.score).slice(0, 2) : []),
    [data],
  );
  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  if (!data?.has_data) {
    return (
      <div>
        <PageHeader eyebrow="Diagnose" title="AI Readiness" />
        <EmptyState icon={BrainCircuit} title="Your AI Readiness Score awaits your assessment"
          whatsMissing="ORBIT needs your responses on data, processes and governance to assess AI readiness."
          whyItMatters="A high readiness score means AI can be applied responsibly — it does not mean you should deploy AI everywhere." />
      </div>
    );
  }

  const ai = data.scores.ai_readiness;
  const dm = data.scores.digital_maturity;

  return (
    <div>
      <PageHeader eyebrow="Diagnose" title="AI Readiness"
        description="How ready your business is to apply AI and automation responsibly — a distinct measure from digital maturity.">
        <SourceBadge type="calculated" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-8 text-center">
          <ScoreRing score={ai.score} level={ai.level} size={168} stroke={12} />
          <p className="mt-4 font-display text-xl font-semibold">{ai.level}</p>
          <p className="mt-1 text-sm text-muted-foreground">AI Readiness Level</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-5 font-display text-base font-semibold">Readiness dimensions</h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {data.ai_dimensions.map((d) => <DimensionBar key={d.key} label={d.label} score={d.score} />)}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <InsightPanel operation="score" payload={{ score_key: "ai_readiness" }} heading="Why this score?" variant="insight" testid="insight-ai-readiness" />
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <h3 className="font-display text-base font-semibold">What would move this score?</h3>
          <SourceBadge type="rule" />
        </div>
        <p className="mb-4 text-sm text-muted-foreground">Strengthening these foundations would improve AI readiness responsibly — before any AI is deployed.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {weakest.map((d) => (
            <div key={d.key} className="rounded-xl border border-border bg-background p-4" data-testid={`move-ai-${d.key}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">{d.label}</span>
                <span className="font-num text-sm text-red-600">{d.score}</span>
              </div>
              <ol className="mt-3 space-y-1.5">
                {(d.improvements || []).map((t, i) => (
                  <li key={t} className="flex gap-2 text-sm text-muted-foreground"><span className="font-num text-xs text-accent">{i + 1}</span>{t}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-blue-500/30 bg-blue-500/5 p-6">
        <div className="mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-blue-600" />
          <h3 className="font-display text-base font-semibold">Why AI Readiness ({ai.score}) differs from Digital Maturity ({dm.score})</h3>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Digital maturity measures how well your business runs on technology today. AI readiness asks a harder
          question: is your <span className="font-medium text-foreground">data</span> clean and available, are your{" "}
          <span className="font-medium text-foreground">processes</span> standardised, and is your{" "}
          <span className="font-medium text-foreground">governance</span> strong enough for AI to be trustworthy?
          A business can be reasonably mature yet not AI-ready because data and standardisation lag. This is exactly
          why ORBIT sequences foundational work before any AI pilot — a high score is permission to proceed carefully,
          not a mandate to deploy AI everywhere.
        </p>
      </div>
    </div>
  );
}
